import { useState } from "react"
import {
  Dialog,
  IconButton,
  Button,
  makeStyles,
  Typography,
} from "@material-ui/core"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faXmark,
  faTriangleExclamation,
  faDownload,
} from "@fortawesome/free-solid-svg-icons"

import { generateSafetyPlanPdf } from "../../../utils/generatePdf"
import { SelectedSection } from "../../../utils/safetyPlanUtils"
import { colors } from "../../../utils/theme"
import SwitchIcon from "../../atoms/SwitchIcon"

interface PdfDownloadDialogProps {
  clientName: string
  selectedOptions: Map<string, SelectedSection>
}

/**
 * Renders a download button that allows clinicians to export a patient's safety
 * plan as a PDF. When clicked, a confirmation dialog is shown warning that the
 * file contains sensitive personal information. The user must acknowledge the
 * warning before the PDF download is enabled.
 *
 * Once confirmed, the component calls `generateSafetyPlanPdf` to generate and
 * download a PDF containing the selected safety plan sections.
 *
 * This component manages its own dialog open/close state and confirmation toggle.
 *
 * @param clientName - The name of the client whose safety plan will appear in the generated PDF.
 * @param selectedOptions - A map of safety plan section identifiers to their selected responses.
 *                          These values are used to populate the generated PDF.
 *
 * @returns An icon button that opens a confirmation dialog and triggers the safety plan PDF download.
 */
export default function SafetyPlanPdfDownloadButton({
  clientName,
  selectedOptions,
}: PdfDownloadDialogProps) {
  const [open, setOpen] = useState(false)
  const [isToggled, setIsToggled] = useState(false)
  const classes = useStyles()

  const downloadPdf = async () => {
    await generateSafetyPlanPdf(selectedOptions, clientName)
  }

  return (
    <>
      <IconButton
        className={classes.downloadPlanButton}
        onClick={() => setOpen(true)}
        style={{ width: "40px" }}
      >
        <FontAwesomeIcon icon={faDownload} size="2xs" />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <div id="downloadContainer" className={classes.downloadPdfContainer}>
          <IconButton
            className={classes.xMark}
            onClick={() => setOpen(false)}
            size="small"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark}></FontAwesomeIcon>
          </IconButton>
          <div className={classes.downloadPdfContent}>
            <FontAwesomeIcon icon={faTriangleExclamation} size="4x" />
            <Typography variant="h2" style={{ lineHeight: "40px" }}>
              Preparing PDF now...
            </Typography>
            <Typography
              variant="body1"
              style={{ color: colors.safetyPlan.graphText, lineHeight: "20px" }}
            >
              Be aware that this file contains the full details of your
              patient’s safety plan which includes sensitive personal
              information. Please store it in a secure location and exercise
              caution when sharing it with others.
            </Typography>
            <div className={classes.downloadConfirmTxtContainer}>
              <Typography className={classes.downloadConfirmTxt}>
                I understand and confirm
              </Typography>
              <SwitchIcon
                checked={isToggled}
                onChange={() => setIsToggled((prev) => !prev)}
              />
            </div>
            <Button
              className={classes.downloadBtn}
              onClick={downloadPdf}
              disabled={!isToggled}
              style={{
                background: isToggled
                  ? colors.primary.shadow
                  : colors.safetyPlan.mediumBlue,
              }}
            >
              <Typography variant="h4" style={{ color: colors.neutral.white }}>
                Confirm
              </Typography>
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  )
}

const useStyles = makeStyles(() => ({
  downloadBtn: {
    display: "flex",
    height: "52px",
    padding: "4px 32px",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    alignSelf: "stretch",
    borderRadius: "200px",
    textTransform: "none",
  },
  downloadPdfContainer: {
    display: "flex",
    width: "400px",
    flexDirection: "column",
    gap: "16px",
    borderRadius: "12px",
    background: colors.neutral.white,
    position: "relative",
    padding: "20px",
  },
  xMark: {
    position: "absolute",
    right: "12.5px",
    top: "6px",
  },
  downloadConfirmTxt: {
    color: colors.neutral.asphalt,
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
  downloadPlanButton: {
    width: "45px",
    padding: "9px 10px 11px 10px",
    marginLeft: "auto",
    background: colors.safetyPlan.grayscale.white75,
    color: colors.neutral.asphalt,
    transition: "0.2s",
    "&:hover": {
      background: colors.safetyPlan.grayscale.white85,
      color: colors.neutral.denim,
      transform: "translateY(-1px)",
    },
  },
  downloadConfirmTxtContainer: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  downloadPdfContent: {
    display: "flex",
    paddingTop: "8px",
    flexDirection: "column",
    alignItems: "center",
    gap: "24px",
    flex: "1 0 0",
  },
}))
